import { ReturnStatusType } from 'returnCenter-api';
import type { ScannerContext, HandlerResult, ScannerHandler } from './types';

// ── Shared guard ──────────────────────────────────────────────────────────────

function requiresOpenReturn(ctx: ScannerContext): HandlerResult {
    if (ctx.view !== 'openReturn') return { ok: false, errorKey: 'notInReturn' };
    if (!ctx.returnSlip) return { ok: false, errorKey: 'noReturnSelected' };
    return { ok: true };
}

// ── Handler map ───────────────────────────────────────────────────────────────
//
// Each entry owns its own validate (gate) + execute logic.
// The mediator (useScannerDispatch) never inspects action semantics — it only
// calls validate → execute and manages the double-confirm flow.

export const scannerHandlers: Record<string, ScannerHandler> = {
    // ── EAN barcode ───────────────────────────────────────────────────────────
    eanScan: {
        validate: (ctx) => {
            if (ctx.view !== 'openReturn') return { ok: false, errorKey: 'eanNotAllowedHere' };
            if (!ctx.refs.incrementRef.current) return { ok: false, errorKey: 'noScanHandlerRegistered' };
            return { ok: true };
        },
        execute: (ctx, _deps, payload) => {
            ctx.refs.incrementRef.current?.(payload as string);
        },
    },

    // ── Navigation ────────────────────────────────────────────────────────────
    newReturn: {
        validate: () => ({ ok: true }),
        execute: (ctx, deps) => {
            deps.setTargetInputMode('barcode');
            // If dirty: navigate without clearing state so the blocker fires and shows
            // the "leave without saving" dialog. Clears happen on dialog confirm.
            if (ctx.hasUnsavedChanges) {
                deps.navigateToHome();
            } else {
                deps.goHome();
            }
        },
    },

    goBackToOrderList: {
        validate: () => ({ ok: true }),
        execute: (_ctx, deps) => {
            deps.goBack();
        },
    },

    openModal: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.refs.openModalRef.current) return { ok: false, errorKey: 'noScanHandlerRegistered' };
            return { ok: true };
        },
        execute: (ctx, _deps, payload) => {
            ctx.refs.openModalRef.current?.((payload as 'orderNumber' | 'articleNumber' | 'barcode') ?? 'barcode');
        },
    },

    // ── Return actions ────────────────────────────────────────────────────────
    completeReturn: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (ctx.buttonStates?.isReturnSlip) return { ok: false, errorKey: 'saveBeforeComplete' };
            if (!ctx.buttonStates?.canComplete) return { ok: false, errorKey: 'cannotCompleteReturn' };
            return { ok: true };
        },
        execute: (_ctx, deps) => deps.handleCompleteReturnSlip(),
        requiresDoubleConfirm: true,
    },

    saveReturn: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.buttonStates?.canSave) return { ok: false, errorKey: 'cannotSaveReturn' };
            return { ok: true };
        },
        execute: (ctx, deps) => {
            deps.handleSaveWithStatus(ctx.returnSlip!.status ?? ReturnStatusType.Created);
        },
    },

    deleteReturn: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.returnSlipId) return { ok: false, errorKey: 'noReturnToDelete' };
            if (!ctx.buttonStates?.canDelete) return { ok: false, errorKey: 'cannotDeleteReturn' };
            return { ok: true };
        },
        // Actual deletion is triggered via openDeleteDialogRef (2nd scan)
        // and confirmDeleteDialogRef (3rd scan), both handled in the mediator.
        execute: () => {},
        requiresDoubleConfirm: true,
        requiresDialogConfirm: true,
    },

    pauseReturn: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.buttonStates?.canPause) return { ok: false, errorKey: 'cannotPauseReturn' };
            return { ok: true };
        },
        execute: (_ctx, deps) => deps.handleSaveWithStatus(ReturnStatusType.OnHold),
        requiresDoubleConfirm: true,
    },

    setMaxQty: {
        validate: (ctx) => {
            if (ctx.view !== 'openReturn') return { ok: false, errorKey: 'notInReturn' };
            if (!ctx.buttonStates?.canModify) return { ok: false, errorKey: 'cannotModifyReturn' };
            if (ctx.hasSplitRows) return { ok: false, errorKey: 'cannotBulkMaxQtyWithSplits' };
            return { ok: true };
        },
        execute: (ctx) => ctx.refs.setMaxQtyRef.current?.(),
        requiresDoubleConfirm: true,
    },

    // ── Logistics ─────────────────────────────────────────────────────────────
    incomingPackage: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.buttonStates?.canSetIncoming) return { ok: false, errorKey: 'cannotSetIncomingPackage' };
            return { ok: true };
        },
        execute: (_ctx, deps) => deps.handleSaveWithStatus(ReturnStatusType.IncomingPackage),
    },

    goodsReceived: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.buttonStates?.canReceiveGoods) return { ok: false, errorKey: 'cannotReceiveGoods' };
            return { ok: true };
        },
        execute: (_ctx, deps) => deps.handleSaveWithStatus(ReturnStatusType.Received),
    },

    setToInProgress: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.buttonStates?.canModify) return { ok: false, errorKey: 'cannotModifyReturn' };
            return { ok: true };
        },
        execute: (_ctx, deps) => deps.handleSaveWithStatus(ReturnStatusType.InProgress),
    },

    createReturn: {
        validate: (ctx) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.buttonStates?.canSave) return { ok: false, errorKey: 'cannotSaveReturn' };
            return { ok: true };
        },
        execute: (_ctx, deps) => deps.handleSaveWithStatus(ReturnStatusType.Created),
    },

    // ── Bulk reason ───────────────────────────────────────────────────────────
    bulkReason: {
        validate: (ctx, payload) => {
            const base = requiresOpenReturn(ctx);
            if (!base.ok) return base;
            if (!ctx.buttonStates?.canModify) return { ok: false, errorKey: 'cannotModifyReturn' };
            if (ctx.hasSplitRows) return { ok: false, errorKey: 'cannotBulkMaxQtyWithSplits' };
            const value = payload as number;
            if (!ctx.reasonCodes.find((rc) => rc.value === value)) {
                return { ok: false, errorKey: 'invalidReasonCode' };
            }
            return { ok: true };
        },
        execute: (ctx, _deps, payload) => {
            ctx.refs.bulkReasonApplyRef.current?.(payload as number);
        },
        requiresDoubleConfirm: true,
    },
};
